import { LoanOffer } from 'embed/constants/models'

export interface RangeFilter {
  min: number
  max: number
}

export interface FilterState {
  selectedLenders: string[] // Selected lenders as company uuids
  loanAmountRange?: RangeFilter // Range of loan amount min and max
  monthlyPaymentRange?: RangeFilter // Range of monthly payment min and max
}

export const applyFilters = (filterState: FilterState, offers: LoanOffer[]) =>
  offers.filter((offer) => isOfferInRange(filterState, offer)).filter((offer) => isOfferInLenders(filterState, offer))

// isOfferInRange Behavior
// |                     | In Monthly Range  | Out of Monthly Range | No Monthly Range |
// | In Amount Range     | ✅                | ❌                    | ✅                |
// | Out of Amount Range | ❌                | ❌                    | ❌                |
// | No Amount Range     | ✅                | ❌                    | ✅                |

const isOfferInRange = (filterState: FilterState, offer: LoanOffer): boolean => {
  const { loanAmountRange, monthlyPaymentRange } = filterState

  if (
    loanAmountRange &&
    offer.maxAmount &&
    (offer.maxAmount < loanAmountRange.min || offer.maxAmount > loanAmountRange.max)
  ) {
    return false // Out of amount range
  }

  if (
    monthlyPaymentRange &&
    offer.meanMonthlyPayment &&
    (offer.meanMonthlyPayment < monthlyPaymentRange.min || offer.meanMonthlyPayment > monthlyPaymentRange.max)
  ) {
    return false // Out of monthly range
  }
  return true // (1,1) (1,3), (3,1), (3,3)
}

const isOfferInLenders = (filterState: FilterState, offer: LoanOffer): boolean => {
  const { selectedLenders } = filterState

  if (selectedLenders === undefined) {
    return true // No lenders set
  } else if (selectedLenders.length === 0) {
    return true // No lenders selected
  } else {
    return offer.originator !== undefined && selectedLenders.indexOf(offer.originator.companyUuid) > -1 ? true : false
  }
}

// Gets smallest and highest loan & monthly payment amounts.
export const getLoanAndMonthlyPaymentRanges = (offers: LoanOffer[]): [RangeFilter, RangeFilter] => {
  if (offers.length) {
    const loanAmounts = offers.map((offer) => (offer.maxAmount && isFinite(offer.maxAmount) ? offer.maxAmount : 0))
    const minLoanAmount = Math.min(...loanAmounts)
    const maxLoanAmount = Math.max(...loanAmounts)
    const monthlyPayments = offers.map((offer) =>
      offer.meanMonthlyPayment && isFinite(offer.meanMonthlyPayment ?? 0) ? offer.meanMonthlyPayment : 0,
    )
    const minMonthlyPayment = Math.min(...monthlyPayments)
    const maxMonthlyPayment = Math.max(...monthlyPayments)
    return [
      { min: minLoanAmount, max: maxLoanAmount },
      { min: minMonthlyPayment, max: maxMonthlyPayment },
    ]
  } else {
    return [
      { min: 0, max: 0 },
      { min: 0, max: 0 },
    ]
  }
}
